/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import { Badge, Button, Dropdown, Flex, Modal, notification } from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  EyeOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteTag } from 'services';
import type { TagSummary } from 'types';
import {
  adjustPageNumber,
  generateExternalPathname,
  generateTableDataSource,
  localizeTagStatus,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    TAGS: { UPDATE_URL: TAGS_UPDATE_URL },
    TAG: { BASE_URL: TAG_BASE_URL },
  },
} = PATHNAME;

type TagsTableProps = {
  loading: boolean;
  total: number;
  tags: TagSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const TagsTable = React.memo<TagsTableProps>(
  ({
    loading,
    total,
    tags,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteTag = useLockFn(async ({ id }: TagSummary) => {
      deleteTag({ id })
        .then(() => {
          handleRefreshData();
          notification.success({
            message: 'برچسب مورد نظر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        });
    });

    const tagsDataSource = generateTableDataSource(tags, 'id');

    return (
      <PrimitiveTable
        scroll={{ x: 600, y: 700 }}
        columns={[
          {
            title: 'نام',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (_, { id, title }) => (
              <AntdRouterLink
                ellipsis
                to={`${TAGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {title}
              </AntdRouterLink>
            ),
          },
          {
            title: 'پیوند یکتا',
            dataIndex: 'slug',
            key: 'slug',
            ellipsis: true,
            render: (_, { slug }) => <bdi>{slug}</bdi>,
          },
          {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            ellipsis: true,
            render: (_, { status }) => {
              const { label, color } = localizeTagStatus(status);
              return (
                <Flex justify='center'>
                  <Badge count={label} color={color} />
                </Flex>
              );
            },
          },
          {
            title: 'نمایه‌گذاری',
            dataIndex: 'seoIndex',
            key: 'seoIndex',
            width: 100,
            ellipsis: true,
            render: (_, { seo }) => {
              const { isIndex } = seo ?? {};
              return (
                <Flex justify='center'>
                  {isIndex ? (
                    <CheckCircleOutlinedIcon className='text-green-500' />
                  ) : (
                    <CloseCircleOutlinedIcon className='text-red-500' />
                  )}
                </Flex>
              );
            },
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id, slug } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'see-tag',
                          label: (
                            <AntdRouterExternalLink
                              href={generateExternalPathname(
                                TAG_BASE_URL,
                                slug,
                              )}
                              className='inline-block py-1 text-inherit'
                            >
                              <EyeOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                مشاهده در فروشگاه
                              </span>
                            </AntdRouterExternalLink>
                          ),
                        },
                        {
                          key: 'update-tag',
                          label: (
                            <AntdRouterLink
                              to={`${TAGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>ویرایش برچسب</span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-tag',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف برچسب مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteTag(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف برچسب</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={tagsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
TagsTable.displayName = 'TagsTable';

export { TagsTable };
